import { candidateOfflineThreshold } from "config/constant";
import { isRestrictedTriggerEvent } from "helpers/proctor-client-trigger-helper";
import { handleError } from "helpers/sentry";
import { AtxNetworkStatus, AtxSdk } from "./atx-sdk";
import { LegacyHostConfig } from "./legacy-config";
import { trace } from "./trace";

type LegacyEventHandler = (data: unknown) => void;

/**
 * The parts of `window.ProctorClient3` this bridge reads off whatever `Shell`
 * installed before it. The global itself stays `any` (hosts hang their own
 * keys off it), so this describes only what is patched here.
 */
export interface LegacyProctorClient {
  eventHandlers?: Record<string, LegacyEventHandler[]>;
  isPhotoIdFailed?: boolean;
  isTextEnvFailed?: boolean;
  [key: string]: unknown;
}

/**
 * Re-points `window.ProctorClient3` at the ATX session, so host pages keep
 * working unchanged after the handoff.
 *
 * The object `Shell` already installed is patched in place rather than
 * replaced: a host that captured the reference early keeps a working one, and
 * `helpers/sentry.ts` reads `window.ProctorClient3.giveControl()` without
 * guarding, so the property must never disappear.
 */
export const bridgeProctorClient = (sdk: AtxSdk, hostConfig: LegacyHostConfig): void => {
  const existing: LegacyProctorClient = window.ProctorClient3 ?? {};
  const eventHandlers: Record<string, LegacyEventHandler[]> = existing.eventHandlers ?? {};

  let offlineRepeat: ReturnType<typeof setInterval> | null = null;
  const stopOfflineRepeat = () => {
    if (offlineRepeat) {
      clearInterval(offlineRepeat);
      offlineRepeat = null;
    }
  };

  // ATX reports a network change once per transition; this client called the
  // host back every `candidateOfflineThreshold` for as long as it stayed
  // offline. Hosts rely on the repeat to decide when to intervene, so it is
  // rebuilt here on top of the single ATX event.
  const unsubscribeNetworkStatus = sdk.session?.on<AtxNetworkStatus>(
    "NetworkStatusChanged",
    ({ status }) => {
      trace(`ATX NetworkStatusChanged: ${status}`);
      if (status !== "offline") {
        stopOfflineRepeat();
        return;
      }
      if (offlineRepeat) return;
      offlineRepeat = setInterval(() => {
        trace("invoking host networkDisconnectionCallback() (offline repeat)");
        hostConfig.networkDisconnectionCallback?.();
      }, candidateOfflineThreshold);
    }
  );

  Object.assign(existing, {
    eventHandlers,
    on(eventName: string, callback: LegacyEventHandler) {
      trace(`host called ProctorClient3.on('${eventName}')`, {
        note: "registered; the alert firehose is not bridged yet, so it will not fire"
      });
      (eventHandlers[eventName] ||= []).push(callback);
    },
    trigger(eventNames: string, data: unknown) {
      // Same guard as this client's own trigger: a host page must not be able
      // to fake pause/terminate/complete.
      if (isRestrictedTriggerEvent(eventNames, data)) {
        trace(`refused restricted ProctorClient3.trigger('${eventNames}')`, data);
        return;
      }
      trace(`host called ProctorClient3.trigger('${eventNames}')`, data);
      eventNames
        .split(" ")
        .forEach((eventName) => eventHandlers[eventName]?.forEach((handler) => handler(data)));
    },
    // Legacy `stop()` ends the session for good, so it maps to ATX's
    // `complete()` — ATX's own `stop()` is resumable.
    stop(finalCallBack?: () => void) {
      trace("host called ProctorClient3.stop() → session.complete()");
      stopOfflineRepeat();
      unsubscribeNetworkStatus?.();
      void sdk.session
        ?.complete()
        .catch((error) => {
          trace("session.complete() rejected", error);
          return undefined;
        })
        .then(() => {
          trace("invoking host finalCallBack()");
          finalCallBack?.();
        });
    },
    pause(code?: string, reason?: string, note?: string) {
      // ATX rejects an empty reason; this client allowed one.
      const resolved = reason || note || code || "Paused by host";
      trace("host called ProctorClient3.pause() → session.pause()", {
        legacy: { code, reason, note },
        sentReason: resolved
      });
      void sdk.session?.pause(resolved).catch(handleError);
    },
    resume() {
      trace("host called ProctorClient3.resume() → session.resume('Resumed by host')");
      void sdk.session?.resume("Resumed by host").catch(handleError);
    },
    giveControl() {
      trace("host called ProctorClient3.giveControl()");
      return hostConfig;
    },
    networkDisconnectionCallback() {
      trace("host called ProctorClient3.networkDisconnectionCallback()");
      hostConfig.networkDisconnectionCallback?.();
    },
    // Carried over rather than reset: these are pre-check outcomes this client
    // may already have recorded, and `routes/workflow.ts` branches on them.
    isPhotoIdFailed: existing.isPhotoIdFailed === true,
    isTextEnvFailed: existing.isTextEnvFailed === true
  });

  window.ProctorClient3 = existing;
  trace("ProctorClient3 re-pointed at the ATX session", {
    sameObjectAsBefore: window.ProctorClient3 === existing
  });
};
